import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ICON_MAP = { rocket: "🚀", calendar: "📅", star: "⭐", zap: "⚡", megaphone: "📢", gift: "🎁", alert: "🔔", fire: "🔥", sparkle: "✨", trophy: "🏆" };

function hasBeenSeen(popup) {
  const { popup_id, frequency } = popup;
  if (frequency === "every_visit") return false;
  if (frequency === "once_session") return sessionStorage.getItem(`popup_${popup_id}`);
  if (frequency === "once_day") {
    const last = localStorage.getItem(`popup_day_${popup_id}`);
    if (!last) return false;
    return new Date().toDateString() === new Date(last).toDateString();
  }
  return false;
}

function markSeen(popup) {
  const { popup_id, frequency } = popup;
  if (frequency === "once_session") sessionStorage.setItem(`popup_${popup_id}`, "1");
  if (frequency === "once_day") localStorage.setItem(`popup_day_${popup_id}`, new Date().toISOString());
}

export default function PopupManager() {
  const [cinematicQueue, setCinematicQueue] = useState([]);
  const [activeCinematic, setActiveCinematic] = useState(null);
  const [bubbles, setBubbles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/popups`).then(res => {
      const active = res.data.filter(p => !hasBeenSeen(p));
      setCinematicQueue(active.filter(p => p.popup_type === "cinematic"));
      setBubbles(active.filter(p => p.popup_type === "bubble"));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (cinematicQueue.length > 0 && !activeCinematic) {
      const timer = setTimeout(() => setActiveCinematic(cinematicQueue[0]), 1500);
      return () => clearTimeout(timer);
    }
  }, [cinematicQueue, activeCinematic]);

  const dismissCinematic = () => {
    if (activeCinematic) {
      markSeen(activeCinematic);
      setActiveCinematic(null);
      setCinematicQueue(q => q.slice(1));
    }
  };

  const dismissBubble = (popup_id) => {
    const bubble = bubbles.find(b => b.popup_id === popup_id);
    if (bubble) markSeen(bubble);
    setBubbles(b => b.filter(p => p.popup_id !== popup_id));
  };

  return (
    <>
      <CinematicPopup popup={activeCinematic} onDismiss={dismissCinematic} />
      <BubblePopups bubbles={bubbles} onDismiss={dismissBubble} />
    </>
  );
}


/* ═══════════════════════════════════════════════════════
   CINEMATIC POPUP — Glassmorphism + Visible Blurred BG
   ═══════════════════════════════════════════════════════ */
function CinematicPopup({ popup, onDismiss }) {
  if (!popup) return null;
  const icon = popup.icon && ICON_MAP[popup.icon] ? ICON_MAP[popup.icon] : null;

  return (
    <AnimatePresence>
      {popup && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Backdrop — light blur so landing page is visible behind */}
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={onDismiss}
          />

          {/* Animated orbs behind the card */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <motion.div
              className="absolute w-[500px] h-[500px] rounded-full"
              style={{ left: "20%", top: "15%", background: "radial-gradient(circle, rgba(0,240,255,0.12) 0%, transparent 60%)", filter: "blur(60px)" }}
              animate={{ x: [0, 60, 0], y: [0, -40, 0], scale: [1, 1.3, 1] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute w-[400px] h-[400px] rounded-full"
              style={{ right: "15%", bottom: "20%", background: "radial-gradient(circle, rgba(112,0,255,0.1) 0%, transparent 60%)", filter: "blur(60px)" }}
              animate={{ x: [0, -50, 0], y: [0, 50, 0], scale: [1.2, 0.9, 1.2] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute w-[300px] h-[300px] rounded-full"
              style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)", background: "radial-gradient(circle, rgba(255,0,200,0.06) 0%, transparent 60%)", filter: "blur(50px)" }}
              animate={{ scale: [0.8, 1.4, 0.8] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          {/* Card */}
          <motion.div
            className="relative z-10 max-w-md w-full"
            initial={{ scale: 0.6, y: 60, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.85, y: 40, opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Outer glow ring */}
            <div className="absolute -inset-[2px] rounded-3xl bg-gradient-to-r from-[#00F0FF] via-[#7000FF] to-[#FF00FF] opacity-60 blur-[3px]" />

            {/* Main card */}
            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0B0B18]/90 backdrop-blur-xl">
              {/* Animated gradient bar at top */}
              <motion.div
                className="h-1"
                style={{ background: "linear-gradient(90deg, #00F0FF, #7000FF, #FF00FF, #00F0FF)", backgroundSize: "300% 100%" }}
                animate={{ backgroundPosition: ["0% center", "100% center"] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />

              {/* Sparkle particles inside card */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 rounded-full bg-[#00F0FF]"
                    style={{ left: `${15 + i * 10}%`, top: `${20 + (i % 3) * 25}%` }}
                    animate={{ opacity: [0, 0.8, 0], scale: [0, 1.5, 0], y: [0, -20, 0] }}
                    transition={{ duration: 2 + i * 0.3, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
                  />
                ))}
              </div>

              <div className="relative p-8 sm:p-10 text-center">
                {/* Close button */}
                <motion.button
                  onClick={onDismiss}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-zinc-500 hover:text-white transition-all"
                  data-testid="close-cinematic-popup"
                  whileHover={{ scale: 1.15, rotate: 90 }}
                >
                  <X size={16} />
                </motion.button>

                {/* Icon with glow ring */}
                {icon && (
                  <motion.div className="relative inline-block mb-6">
                    <motion.div
                      className="absolute inset-0 rounded-full bg-[#00F0FF]/15"
                      animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <motion.div
                      className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00F0FF]/10 to-[#7000FF]/10 border border-white/10 flex items-center justify-center text-4xl"
                      animate={{ y: [0, -6, 0], rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                      {icon}
                    </motion.div>
                  </motion.div>
                )}

                {/* Image */}
                {popup.image_url && (
                  <motion.div
                    className="mb-6 rounded-2xl overflow-hidden border border-white/5"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <img src={popup.image_url} alt="" className="w-full max-h-48 object-cover" />
                  </motion.div>
                )}

                {/* Title */}
                <motion.h2
                  className="font-outfit font-black text-3xl sm:text-4xl mb-3 text-white"
                  initial={{ y: 25, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  {popup.title}
                </motion.h2>

                {/* Decorative line */}
                <motion.div
                  className="w-16 h-0.5 mx-auto mb-5 rounded-full"
                  style={{ background: "linear-gradient(90deg, #00F0FF, #7000FF)" }}
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 64, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                />

                {/* Message */}
                <motion.p
                  className="text-zinc-400 font-dm-sans text-base leading-relaxed mb-8 max-w-sm mx-auto"
                  initial={{ y: 15, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.35, duration: 0.5 }}
                >
                  {popup.message}
                </motion.p>

                {/* CTA */}
                {popup.cta_text && (
                  <motion.div
                    initial={{ y: 15, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <a
                      href={popup.cta_url || "#"}
                      onClick={onDismiss}
                      data-testid="cinematic-cta-btn"
                    >
                      <motion.div
                        className="relative inline-flex items-center gap-2 font-outfit font-bold px-8 py-3.5 rounded-full text-base overflow-hidden"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        {/* Button glow bg */}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#00F0FF] to-[#7000FF]" />
                        {/* Shimmer sweep */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent"
                          style={{ width: "50%" }}
                          animate={{ x: ["-100%", "300%"] }}
                          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1, ease: "easeInOut" }}
                        />
                        <span className="relative text-black">{popup.cta_text}</span>
                        <ArrowRight size={18} className="relative text-black" />
                      </motion.div>
                    </a>
                  </motion.div>
                )}

                {!popup.cta_text && (
                  <motion.button
                    onClick={onDismiss}
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-zinc-500 hover:text-white font-dm-sans text-sm transition-colors underline underline-offset-4"
                  >
                    Dismiss
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


/* ═══════════════════════════════════════════════════════
   BUBBLE POPUP — Glassmorphism Expandable
   ═══════════════════════════════════════════════════════ */
function BubblePopups({ bubbles, onDismiss }) {
  const [expanded, setExpanded] = useState(null);

  if (bubbles.length === 0) return null;

  return (
    <div className="fixed bottom-24 right-6 z-[190] flex flex-col items-end gap-3">
      {/* Expanded card */}
      <AnimatePresence>
        {expanded && (() => {
          const popup = bubbles.find(b => b.popup_id === expanded);
          if (!popup) return null;
          const icon = popup.icon && ICON_MAP[popup.icon] ? ICON_MAP[popup.icon] : null;

          return (
            <motion.div
              key="bubble-card"
              initial={{ opacity: 0, y: 30, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.85 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-[340px]"
              data-testid="bubble-card"
            >
              {/* Outer glow */}
              <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-[#00F0FF]/40 via-[#7000FF]/20 to-[#FF00FF]/30 blur-[2px]" />

              <div className="relative bg-[#0C0C18]/90 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-[0_25px_80px_-20px_rgba(0,240,255,0.2)]">
                {/* Top gradient bar */}
                <motion.div
                  className="h-0.5"
                  style={{ background: "linear-gradient(90deg, #00F0FF, #7000FF, #FF00FF, #00F0FF)", backgroundSize: "300% 100%" }}
                  animate={{ backgroundPosition: ["0% center", "100% center"] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                />

                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {icon && (
                        <motion.div
                          className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF]/15 to-[#7000FF]/15 border border-white/10 flex items-center justify-center text-xl"
                          animate={{ rotate: [0, 5, -5, 0] }}
                          transition={{ duration: 4, repeat: Infinity }}
                        >
                          {icon}
                        </motion.div>
                      )}
                      <div>
                        <h3 className="font-outfit font-bold text-white text-sm">{popup.title}</h3>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Sparkles size={9} className="text-[#00F0FF]" />
                          <span className="text-[10px] text-[#00F0FF] font-dm-sans font-semibold">Featured</span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => { setExpanded(null); onDismiss(popup.popup_id); }}
                      className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-zinc-500 hover:text-white transition-all"
                      data-testid="close-bubble-card">
                      <X size={14} />
                    </button>
                  </div>

                  {/* Image */}
                  {popup.image_url && (
                    <div className="mb-4 rounded-xl overflow-hidden border border-white/5">
                      <img src={popup.image_url} alt="" className="w-full max-h-32 object-cover" />
                    </div>
                  )}

                  {/* Message */}
                  <p className="text-zinc-400 font-dm-sans text-sm leading-relaxed mb-5">{popup.message}</p>

                  {/* CTA */}
                  {popup.cta_text && (
                    <a
                      href={popup.cta_url || "#"}
                      onClick={() => { setExpanded(null); onDismiss(popup.popup_id); }}
                      data-testid="bubble-cta-btn"
                    >
                      <motion.div
                        className="relative flex items-center justify-center gap-1.5 w-full py-2.5 rounded-xl text-sm font-dm-sans font-bold overflow-hidden"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#00F0FF] to-[#7000FF]" />
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          style={{ width: "40%" }}
                          animate={{ x: ["-100%", "350%"] }}
                          transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 1.5, ease: "easeInOut" }}
                        />
                        <span className="relative text-black">{popup.cta_text}</span>
                        <ArrowRight size={14} className="relative text-black" />
                      </motion.div>
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Bubble triggers */}
      {!expanded && bubbles.map((bubble, i) => (
        <motion.button
          key={bubble.popup_id}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 2 + i * 0.5, type: "spring", stiffness: 300, damping: 20 }}
          onClick={() => setExpanded(bubble.popup_id)}
          className="relative group"
          data-testid={`bubble-trigger-${bubble.popup_id}`}
        >
          {/* Outer pulse */}
          <motion.div
            className="absolute -inset-2 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(0,240,255,0.15) 0%, transparent 70%)" }}
            animate={{ scale: [1, 1.8, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Second pulse ring */}
          <motion.div
            className="absolute -inset-1 rounded-full border border-[#00F0FF]/20"
            animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          />

          {/* Bubble body */}
          <motion.div
            className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00F0FF] via-[#5B3FFF] to-[#7000FF] flex items-center justify-center shadow-[0_0_35px_rgba(0,240,255,0.3)]"
            whileHover={{ scale: 1.1, shadow: "0 0 50px rgba(0,240,255,0.5)" }}
          >
            {bubble.icon && ICON_MAP[bubble.icon] ? (
              <span className="text-xl">{ICON_MAP[bubble.icon]}</span>
            ) : (
              <MessageCircle size={22} className="text-white" />
            )}
          </motion.div>

          {/* Notification badge */}
          <motion.div
            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full border-2 border-[#0C0C14] flex items-center justify-center"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className="text-[9px] text-white font-bold">1</span>
          </motion.div>
        </motion.button>
      ))}
    </div>
  );
}
